import { formatUnits } from "viem"

import type { IEventBus } from "@/domains/events"
import type { ILockRepository } from "@/domains/lock/interfaces"
import type { LockOnChainTransport } from "@/domains/lock/lock-on-chain-transport"
import type { ITokenLookup } from "@/domains/tokens/types"
import type { IVenammStore } from "@/domains/venamm"
import { LcAddress } from "@/domains/core"

import type { LockVenammQuoteCache } from "./lock-venamm-quote-cache"
import type { VenammQuoteData } from "./types"

export class LockVenammQuoteService {
  constructor(
    private cache: LockVenammQuoteCache,
    private lockOnChainTransport: LockOnChainTransport,
    private venammStore: IVenammStore,
    private tokenLookup: ITokenLookup,
    private lockRepository: ILockRepository,
    private eventBus: IEventBus,
  ) {
    // Subscribe to events to invalidate cache
    this.subscribeToEvents()
  }

  /**
   * Fetch quote for a single lock
   * Used by venamm sell page for single lock display
   */
  async fetchQuoteForLock(lockId: number): Promise<VenammQuoteData | null> {
    try {
      const offer = await this.lockOnChainTransport.getVenammQuote(lockId)
      if (offer === null) {
        return null
      }

      const lock = this.lockRepository.find(lockId)
      if (!lock) {
        console.error(`Lock ${lockId} not found in repository`)
        return null
      }

      // Get governance token decimals
      const governanceTokenAddress = this.lockOnChainTransport["addresses"].governanceToken
      const governanceToken = this.tokenLookup.find(new LcAddress(governanceTokenAddress))
      const decimals = governanceToken?.decimals ?? 18

      const value = Number(formatUnits(offer, decimals))
      const lockAmount = lock.lockAmount

      // Calculate rate and haircut
      const rate = lockAmount > 0 ? value / lockAmount : 0
      const haircut = (1 - rate) * 100

      // Check if there's enough supply to sell
      const canSell = this.venammStore.availableTokenSupply >= offer

      const quoteData: VenammQuoteData = {
        lockId,
        offer,
        value,
        haircut,
        rate,
        canSell,
        fetchedAt: Date.now(),
      }

      // Cache the quote
      this.cache.setQuote(lockId, quoteData)

      return quoteData
    } catch (error) {
      console.error(`Failed to fetch Venamm quote for Lock #${lockId}:`, error)
      return null
    }
  }

  /**
   * Fetch quotes for multiple locks
   * Used by lock table for batch display
   * For now, fetches sequentially (will be optimized with batch contract later)
   */
  async fetchQuotesForLocks(lockIds: number[]): Promise<void> {
    // Fetch in batches of 5 to avoid overwhelming RPC
    const BATCH_SIZE = 5
    for (let i = 0; i < lockIds.length; i += BATCH_SIZE) {
      const batch = lockIds.slice(i, i + BATCH_SIZE)
      await Promise.allSettled(batch.map((lockId) => this.fetchQuoteForLock(lockId)))

      // Small delay between batches
      if (i + BATCH_SIZE < lockIds.length) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
    }
  }

  /**
   * Get cached quote without fetching
   */
  getQuote(lockId: number): VenammQuoteData | null {
    return this.cache.getQuote(lockId)
  }

  /**
   * Check if quote is stale (older than 60 seconds)
   */
  isQuoteStale(lockId: number): boolean {
    const quote = this.cache.getQuote(lockId)
    if (!quote) return true

    const CACHE_TTL = 60_000 // 60 seconds
    return Date.now() - quote.fetchedAt > CACHE_TTL
  }

  /**
   * Refresh quote if stale
   */
  async refreshQuoteIfStale(lockId: number): Promise<VenammQuoteData | null> {
    if (!this.isQuoteStale(lockId)) {
      return this.cache.getQuote(lockId)
    }

    return await this.fetchQuoteForLock(lockId)
  }

  /**
   * Subscribe to domain events to invalidate cache when locks change
   */
  private subscribeToEvents(): void {
    // When lock amount increases, clear its quote (it's now different)
    this.eventBus.subscribe("lock.amount-increased", (event: any) => {
      this.cache.clearQuote(event.lockId)
    })

    // When lock duration is extended, clear its quote (voting power changes)
    this.eventBus.subscribe("lock.duration-extended", (event: any) => {
      this.cache.clearQuote(event.lockId)
    })

    // When lock is sold via venamm, clear its quote (lock no longer exists)
    this.eventBus.subscribe("venamm-sell.sale-completed", (event: any) => {
      this.cache.clearQuote(event.lockId)
    })

    // When lock is transferred out, clear its quote (no longer user's lock)
    this.eventBus.subscribe("lock.transferred-out", (event: any) => {
      this.cache.clearQuote(event.lockId)
    })

    // When wallet disconnects, clear all quotes
    this.eventBus.subscribe("wallet.disconnected", () => {
      this.cache.clearAll()
    })
  }
}
